import { Moon, Sun, Monitor, Palette, Check } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useThemeShortcut } from "@/hooks/useThemeShortcut";
import { useAccentColor, accentColors, type AccentColor } from "@/hooks/useAccentColor";
import { cn } from "@/lib/utils";

export function ThemeToggle() {
  const { setTheme, theme } = useTheme();
  const { accentColor, setAccentColor } = useAccentColor();
  
  // Enable keyboard shortcut
  useThemeShortcut();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 hover:scale-110 active:scale-95 transition-transform duration-200">
          <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover border-border w-48">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Theme <span className="text-[10px] opacity-60">(Ctrl+Shift+T)</span>
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => setTheme("light")} className="cursor-pointer">
          <Sun className="mr-2 h-4 w-4" />
          Light
          {theme === "light" && <Check className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")} className="cursor-pointer">
          <Moon className="mr-2 h-4 w-4" />
          Dark
          {theme === "dark" && <Check className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")} className="cursor-pointer">
          <Monitor className="mr-2 h-4 w-4" />
          System
          {theme === "system" && <Check className="ml-auto h-4 w-4" />}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuLabel className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Palette className="h-3 w-3" />
          Accent Color
        </DropdownMenuLabel>
        <div className="grid grid-cols-6 gap-1 p-2">
          {(Object.keys(accentColors) as AccentColor[]).map((color) => (
            <button
              key={color}
              onClick={() => setAccentColor(color)}
              aria-label={`Set accent color to ${accentColors[color].name}`}
              className={cn(
                "w-6 h-6 rounded-full transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-popover",
                accentColor === color && "ring-2 ring-offset-2 ring-offset-popover scale-110"
              )}
              style={{ 
                backgroundColor: accentColors[color].preview,
                boxShadow: accentColor === color ? `0 0 8px ${accentColors[color].preview}` : undefined
              }}
              title={accentColors[color].name}
            />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
